<template>
  <div v-if="photo">
    <v-container v-if="photoDetailsVisible" class="py-0 mb-6">
      <v-row justify="space-between">
        <v-col cols="12" md="6">
          <div class="d-flex">
            <div class="avatar-wrapper mr-6">
              <v-dialog
                v-if="
                  photo.statusName !== types.photo.status.inCuration ||
                    (me && me.isAdmin) ||
                    (me && me.role === types.user.role.curator)
                "
                v-model="photographerDialog"
                width="300"
              >
                <template #activator="{ on }">
                  <div>
                    <v-avatar size="48px" class="pointer" v-on="on">
                      <img
                        v-if="photo.photographerAvatarUrl"
                        :src="photo.photographerAvatarUrl"
                        :alt="`${photo.photographerName} avatar.`"
                      />
                    </v-avatar>
                    <div
                      v-if="photo.userHasAnActiveSubscription && photo.userSubscriptionType !== types.subscription.type.basic"
                      class="text-center"
                    >
                      <span
                        :class="
                          $vuetify.theme.dark
                            ? 'black--text'
                            : 'secondary--text'
                        "
                        class="pro-badge primary mt-1 font-weight-bold text-caption"
                      >
                        <small>
                          {{
                            photo.userSubscriptionType ===
                            types.subscription.type.pro
                              ? 'PRO'
                              : 'PRO +'
                          }}
                        </small>
                      </span>
                    </div>
                  </div>
                </template>
                <PhotographerModal :me="me" :photo="photo" :info="info" />
              </v-dialog>
              <v-avatar v-else size="48px" class="pointer">
                <img src="/images/100asa-round-icon.png" alt="100ASA icon." />
              </v-avatar>
            </div>
            <div class="w-100-percentage">
              <h1 class="minor-line-height text-h5 font-weight-bold">
                {{ photo.title }}
              </h1>
              <h2 class="text-subtitle-1">
                by
                <v-dialog
                  v-if="
                    photo.statusName !== types.photo.status.inCuration ||
                      (me && me.isAdmin) ||
                      (me && me.role === types.user.role.curator)
                  "
                  v-model="photographerDialog"
                  width="300"
                >
                  <template #activator="{ on }">
                    <span class="text-underline font-weight-bold" v-on="on">
                      {{ photo.photographerName }}
                    </span>
                  </template>
                  <PhotographerModal :me="me" :photo="photo" :info="info" />
                </v-dialog>
                <v-tooltip v-else top max-width="300">
                  <template #activator="{ on }">
                    <span class="font-weight-bold" v-on="on">Hidden Name</span>
                  </template>
                  <span>
                    Photographer's name will be visible at the end of the
                    curation period
                  </span>
                </v-tooltip>
              </h2>
              <template
                v-if="photo.photographerRole === types.user.role.ambassador"
              >
                <h3 class="text-subtitle-2 font-weight-bold">
                  AMBASSADOR
                </h3>
              </template>
              <template v-if="photo.statusName === types.photo.status.album">
                <h3 class="text-subtitle-2">
                  Uploaded on a public
                  <span class="font-weight-bold">album</span>
                </h3>
              </template>
              <template v-else>
                <h3
                  v-if="photo.statusName === types.photo.status.inCuration"
                  class="text-subtitle-2"
                >
                  Uploaded for <span class="font-weight-bold">curation</span>
                  {{ photo.uploadDate | timeago }}
                </h3>
                <h3
                  v-if="photo.statusName === types.photo.status.uploaded"
                  class="text-subtitle-2"
                >
                  Uploaded
                  {{ photo.uploadDate | timeago }}
                </h3>
                <h3 v-else class="text-subtitle-2">
                  {{ photo.hasBeenPromoted ? 'Promoted to' : 'Published on' }}
                  the
                  <span class="font-weight-bold">{{
                    $asa.getFriendlyPhotoStatusName(photo.statusName)
                  }}</span>
                  gallery
                  <span
                    v-if="photo.statusName === types.photo.status.inCuration"
                  >
                    {{ photo.uploadDate | timeago }}
                  </span>
                  <span v-else>{{ photo.publishDate | timeago }}</span>
                </h3>
              </template>
              <h4 class="text-caption">
                Category:
                <span
                  small
                  class="font-weight-bold text-uppercase link"
                  text
                  @click="
                    setAndGo(photo.categoryName.toLowerCase(), 'category')
                  "
                >
                  {{ photo.categoryName }}
                </span>
              </h4>
              <h5 v-if="photo.city && photo.country" class="text-caption">
                Location:
                <v-dialog
                  ref="dialog"
                  v-model="locationDialog"
                  width="600"
                  @input="isLocationDialogVisible"
                >
                  <template #activator="{ on: dialog }">
                    <v-tooltip top>
                      <template #activator="{ on: tooltip }">
                        <span
                          small
                          class="font-weight-bold text-uppercase link"
                          text
                          v-on="{ ...dialog, ...tooltip }"
                        >
                          {{ photo.city }} - {{ photo.country }}
                        </span>
                      </template>
                      <span>
                        View photo location on map
                      </span>
                    </v-tooltip>
                  </template>
                  <v-card outlined>
                    <v-card-text class="pt-6">
                      <div id="map" class="map"></div>
                    </v-card-text>
                  </v-card>
                </v-dialog>
              </h5>
              <h5
                v-if="photo.isCreativeEdit"
                class="text-caption mb-4 font-weight-bold"
              >
                Creative Edit
              </h5>
              <div class="mt-4">
                <p>{{ photo.description }}</p>
              </div>
              <div
                v-if="photo && photo.tags && photo.tags.length > 0"
                class="my-6"
              >
                <h5 class="text-body-1 d-block font-weight-bold">Tagged as:</h5>
                <nuxt-link
                  v-for="(tag, i) in photo.tags"
                  :key="i"
                  :to="setAndGo(tag.toLowerCase(), 'tags', true)"
                  class="text-body-2 no-underline font-italic d-inline-block mr-2"
                >
                  #{{ tag }}
                </nuxt-link>
              </div>
              <div v-if="photo.forSale">
                <v-btn large depressed dark @click="openViewPrintDialog(photo)">
                  Buy this print
                </v-btn>
              </div>
              <div
                v-if="
                  me &&
                    me.id !== photo.photographerId &&
                    (me.isAdmin || me.role === types.user.role.curator)
                "
                class="mt-6"
              >
                <v-alert
                  dismissible
                  color="bluezone secondary--text"
                  icon="fas fa-user-secret"
                  dark
                  close-icon="fal fa-times-circle"
                >
                  <h3 class="mb-2">
                    Only visible to curators:
                  </h3>
                  <ul class="text-body-2 mb-3">
                    <li>Photo total score: {{ photo.percentageScore }}%</li>
                    <template
                      v-if="photo.statusName === types.photo.status.inCuration"
                    >
                      <li>
                        Total curations:
                        {{ photo.numberOfPeopleCurationClient || 0 }}
                      </li>
                      <li>
                        Published to Prime votes: {{ photo.publishNumberOfVotes || 0 }}
                      </li>
                      <li>
                        Demoting votes: {{ photo.rejectNumberOfVotes || 0 }}
                      </li>
                    </template>
                    <li v-if="photo.statusName === types.photo.status.prime || photo.statusName === types.photo.status.inspiration">
                      Votes for Elite: {{ photo.numberOfPromoteVotesElite }} / {{ photo.numberOfVoteToBePromotedElite }}
                    </li>
                    <li v-if="photo.statusName === types.photo.status.prime || photo.statusName === types.photo.status.inspiration">
                      Elite pool days in-voting:
                      {{ photo.numberOfDaysInVoteForElite || 0 }}
                    </li>
                    <li v-if="photo.statusName === types.photo.status.prime">
                      Votes for Inspiration: {{ photo.numberOfPromoteVotesInspiration }} / {{ photo.numberOfVoteToBePromotedInspiration }}
                    </li>
                    <li v-if="photo.statusName === types.photo.status.prime">
                      Inspiration pool days in-voting:
                      {{ photo.numberOfDaysInVoteForInspiration || 0 }}
                    </li>
                  </ul>
                  <Curator-notes-dialog :me="me" :photo="photo" />
                </v-alert>
              </div>
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="5">
          <h5 class="text-body-1 d-block font-weight-bold">EXIF:</h5>
          <v-row class="mt-0">
            <v-col cols="6" class="pa-0">
              <div class="d-inline-flex align-center">
                <i class="icon-camera font-size-48 mr-2"></i>
                <span class="text-body-2">{{
                  photo.metadata && photo.metadata.model
                    ? photo.metadata.model
                    : 'N/A'
                }}</span>
              </div>
            </v-col>
            <v-col cols="6" class="pa-0">
              <div class="d-inline-flex align-center">
                <i class="icon-lens font-size-48 mr-2"></i>
                <span class="text-body-2">{{
                  photo.metadata && photo.metadata.focalLength
                    ? photo.metadata.focalLength
                    : 'N/A'
                }}</span>
              </div>
            </v-col>
            <v-col cols="6" class="pa-0">
              <div class="d-inline-flex align-center">
                <i class="icon-aperture font-size-48 mr-2"></i>
                <span class="text-body-2">{{
                  photo.metadata && photo.metadata.aperture
                    ? photo.metadata.aperture
                    : 'N/A'
                }}</span>
              </div>
            </v-col>
            <v-col cols="6" class="pa-0">
              <div class="d-inline-flex align-center">
                <i class="icon-shutter font-size-48 mr-2"></i>
                <span class="text-body-2">{{
                  photo.metadata && photo.metadata.exposureTime
                    ? photo.metadata.exposureTime
                    : 'N/A'
                }}</span>
              </div>
            </v-col>
            <v-col cols="6" class="pa-0">
              <div class="d-inline-flex align-center">
                <i class="icon-iso font-size-48 mr-2"></i>
                <span class="text-body-2">{{
                  photo.metadata && photo.metadata.iso
                    ? photo.metadata.iso
                    : 'N/A'
                }}</span>
              </div>
            </v-col>
            <v-col cols="6" class="pa-0">
              <div class="d-inline-flex align-center">
                <i class="fal fa-clock fa-fix-size mr-2"></i>
                <span v-if="formattedExifDateTime" class="text-body-2">{{
                  formattedExifDateTime | printdate
                }}</span>
                <span v-else class="text-body-2">N/A</span>
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
    <ViewPrintDialog />
  </div>
</template>

<script>
import moment from 'moment'
import types from '@/utils/types'
import { photoLocationDialogMixin } from '@/mixins/photoLocationDialogMixin'
import { EventBus } from '@/plugins/event-bus'

export default {
  name: 'PhotoDetails',
  filters: {
    timeago: date => {
      return moment
        .utc(date)
        .local()
        .fromNow()
    },
    printdate: date => {
      return moment(date).format('D MMMM YYYY - HH:mm')
    }
  },
  components: {
    PhotographerModal: () => import('@/components/photo/PhotographerModal.vue'),
    ViewPrintDialog: () => import('@/components/prints/ViewPrintDialog.vue'),
    CuratorNotesDialog: () => import('@/components/photo/CuratorNotesDialog.vue')
  },
  mixins: [photoLocationDialogMixin],
  props: {
    me: {
      type: [Object, Boolean],
      default: () => {}
    },
    photo: {
      type: Object,
      default: () => {}
    },
    info: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      types
    }
  },
  computed: {
    formattedExifDateTime() {
      let dateTime = null
      if (this.photo.metadata && this.photo.metadata.dateTime) {
        dateTime = moment(this.photo.metadata.dateTime, 'YYYY:MM:DD HH:mm:ss')
      }
      return dateTime
    }
  },
  mounted() {
    EventBus.$on('arePhotoDetailsVisible', photoDetailsVisible => {
      this.photoDetailsVisible = photoDetailsVisible
    })
  },
  methods: {
    openViewPrintDialog(photo) {
      this.$store.dispatch('prints/openDialog', photo)
    },
    setAndGo(value, type, link) {
      let path = ''
      switch (this.$route.query.from) {
        case 'inspiration':
          path = '/inspiration'
          break
        case 'elite':
          path = '/elite'
          break
        case 'upcoming':
          path = '/upcoming'
          break
        case 'opening':
          path = '/opening'
          break
        case 'communityAwarded':
          path = '/community-awarded'
          break
        case 'prime':
        default:
          path = '/prime'
          break
      }
      path += `?${type}=${value}`
      if (link) {
        return path
      }
      this.$router.push(path)
    }
  }
}
</script>

<style scoped>
.font-size-48 {
  font-size: 48px;
}
.fa-fix-size {
  font-size: 26px;
  line-height: 48px;
  width: 48px;
  text-align: center;
}
.text-underline {
  cursor: pointer;
  text-decoration: underline;
}
.pointer {
  cursor: pointer;
}
.link {
  cursor: pointer;
  text-decoration: underline;
}
.avatar-wrapper {
  flex: 0 !important;
  width: 42px;
}
.avatar-wrapper img {
  width: 42px;
  height: 42px;
}
.pro-badge {
  border-radius: 4px;
  display: block;
  width: 48px;
}
.map {
  overflow: hidden;
  position: relative;
  width: 100%;
  height: 300px;
}
</style>
